import React, { useCallback, useMemo, useState } from 'react';
import styles from './table.module.scss';
import IconDown from '../../assets/icons/arrow_down.svg?react';
import Spinner from '@components/spinner/Spinner';

type Sorting = {
  key: string;
  asc: (a: any, b: any) => number;
  desc: (a: any, b: any) => number;
};

type SortingState = {
  key: string;
  dir: 'asc' | 'desc';
  func: (a: any, b: any) => number;
}

export type HeaderColumn = {
  title: string;
  sort?: Sorting;
  center?: boolean;
  right?: boolean;
};

export type BodyColumn<T = any> = {
  key: string | ((data: T, column: BodyColumn<T>, options?: { isRowHovered?: boolean }) => React.ReactNode);
  className?: string;
}

type Props<T> = {
  header: HeaderColumn[];
  body: BodyColumn<T>[];
  data: T[];
  keyExtractor?: (item: T) => string;
  color?: 'grey' | 'green';
  noItemsText?: string;
  custom?: boolean | null;
  isLoading?: boolean;
  rowClass?: string;
  smallFont?: boolean;
};

const noop = () => void 0;

function Table<T>(props: Props<T>) {
  const { header, data, body, keyExtractor, color = 'grey', custom, isLoading, rowClass, smallFont } = props;

  const getKey = useCallback((item, index) => {
    if (keyExtractor) {
      return keyExtractor(item);
    }
    return index.toString();
  }, [keyExtractor]);

  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [sorting, setSorting] = useState<SortingState | undefined>();

  const tableClasses = useMemo(() => {
    const classes = [styles.table, styles[color]];
    return classes.join(' ');
  }, [color]);

  const handleChangeSorting = useCallback((field: Sorting) => {
    if (!sorting) {
      return setSorting({
        key: field.key,
        dir: 'asc',
        func: field.asc,
      });
    }

    if (sorting.key === field.key) {
      if (sorting.dir === 'asc') {
        return setSorting({
          key: field.key,
          dir: 'desc',
          func: field.desc,
        });
      } else {
        return setSorting(undefined);
      }
    } else {
      return setSorting({
        key: field.key,
        dir: 'asc',
        func: field.asc,
      });
    }
  }, [sorting, setSorting]);

  const sortedData = useMemo(() => {
    if (!sorting) return data;
    return [...data].sort(sorting.func);
  }, [data, sorting]);

  return (
    <div>
      {isLoading ? (
        <div style={{ paddingTop: 50 }}>
          <Spinner />
        </div>
      ) : null}
      <table className={tableClasses}>
        <thead className={styles.header}>
        <tr>
          {header.map((h, index) => {
            const hasSorting = !!h.sort;
            const isCenter = !!h.center;
            const isRight = !!h.right;
            const sorted = sorting?.key && sorting.key === h.sort?.key;
            const asc = sorting?.dir === 'asc';
            const onClick = hasSorting ? () => handleChangeSorting(h.sort!) : noop;
            return (
              <th key={`${index}_${h.title}`} className={`${styles.headerCol} ${hasSorting ? styles.headerColWithSort : ''} ${isCenter ? styles.headerCenter : ''} ${isRight ? styles.headerRight : ''}`}
                  onClick={onClick}>
                <div className={`${styles.sorting} ${asc ? styles.sortingAsc : ''} ${isCenter ? styles.headerCenter : ''} ${isRight ? styles.headerRight : ''}`}>
                  {h.title}
                  {sorted && <IconDown/>}
                </div>
              </th>
            );
          })}
        </tr>
        </thead>
        <tbody>
        {sortedData.map((row, index) => {
          const isRowHovered = hoveredRowIndex === index;
          return (
            <tr 
              key={`${getKey(row, index)}_${index}`} 
              className={`${styles.row}${rowClass ? ` ${rowClass}` : ''}`}
              onMouseEnter={() => setHoveredRowIndex(index)}
              onMouseLeave={() => setHoveredRowIndex(null)}
            >
              {body.map((col, ind) => {
                // Pass isRowHovered to the content function
                const content = typeof col.key === 'string' 
                  ? (row as any)[col.key] 
                  : col.key(row, col, { isRowHovered });
                
                return (
                  <td key={`${index}_${ind}`} className={[custom ? styles.customColumn : styles.column, smallFont ? styles.smallColumnFont : '', col.className || ''].join(' ')}>{content}</td>
                );
              })}
            </tr>
          )
        })}
        </tbody>
      </table>
      {sortedData?.length === 0 && <div className={styles.noItemsText}>{props.noItemsText ? props.noItemsText : 'No items.'}</div>}
    </div>
  );
}

export default Table;
